import "katex/dist/katex.min.css";
import { Check, Copy, ImageIcon } from "lucide-react";
import { memo, useCallback, useEffect, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

type Highlighter = (code: string, lang: string, dark: boolean) => Promise<string>;
let highlighterPromise: Promise<Highlighter> | null = null;

/** Shiki is large, so it is loaded on first use. */
function loadHighlighter(): Promise<Highlighter> {
  highlighterPromise ??= import("shiki/bundle/web").then(({ codeToHtml, bundledLanguages }) => {
    return async (code: string, lang: string, dark: boolean) => {
      const known = lang in bundledLanguages ? lang : "text";
      return codeToHtml(code, { lang: known, theme: dark ? "github-dark-default" : "github-light-default" });
    };
  });
  return highlighterPromise;
}

export function CopyButton({ text, className, iconClassName }: { text: string; className?: string; iconClassName?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className={className ?? "rounded p-1 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900 dark:hover:bg-zinc-700 dark:hover:text-zinc-100"}
      onClick={async () => {
        await navigator.clipboard?.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
      aria-label="Copy"
      title="Copy"
    >
      {done ? <Check size={14} className={iconClassName} /> : <Copy size={14} className={iconClassName} />}
    </button>
  );
}

export type CodeAction = (lang: string, code: string) => ReactNode;
export type CodeView = (lang: string, code: string, onFail: () => void) => ReactNode;

function CodeBlock({ lang, code, streaming, action, view }: { lang: string; code: string; streaming?: boolean; action?: CodeAction; view?: CodeView }) {
  const [html, setHtml] = useState<string | null>(null);
  const [showCode, setShowCode] = useState(false);
  const [failed, setFailed] = useState(false);
  const onFail = useCallback(() => setFailed(true), []);
  const shown = streaming || failed ? null : view?.(lang, code, onFail);
  const dark = document.documentElement.classList.contains("dark");
  useEffect(() => {
    if (streaming) return;
    let alive = true;
    loadHighlighter()
      .then((hl) => hl(code, lang, dark))
      .then((h) => alive && setHtml(h))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [code, lang, dark, streaming]);

  return (
    <div className="not-prose my-3 overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800">
      <div className="flex items-center justify-between bg-zinc-100 px-3 py-1 text-xs text-zinc-500 dark:bg-zinc-900">
        <span className="font-mono">{lang || "text"}</span>
        <div className="flex items-center gap-1">
          {shown && (
            <button type="button" className="rounded px-1.5 py-0.5 text-xs hover:bg-zinc-200 dark:hover:bg-zinc-700" onClick={() => setShowCode(!showCode)}>
              {showCode ? "Picture" : "Code"}
            </button>
          )}
          {action?.(lang, code)}
          <CopyButton text={code} />
        </div>
      </div>
      {shown && !showCode ? (
        shown
      ) : html && !streaming ? (
        <div className="overflow-x-auto text-[13px] [&_pre]:!bg-transparent [&_pre]:p-3" dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <pre className="overflow-x-auto p-3 text-[13px]">
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
}

/** Where an image in model output points, if it is not this server or inline data. */
function externalHost(src: string | undefined): string | null {
  if (!src) return null;
  try {
    const url = new URL(src, window.location.href);
    if (url.protocol === "data:" || url.protocol === "blob:" || url.origin === window.location.origin) return null;
    return url.host || url.protocol;
  } catch {
    return "invalid address";
  }
}

/**
 * Images in model output. Ones from other hosts load only when clicked: otherwise a prompt
 * injection (in a web page or tool result the model read) could have the model write an image
 * whose address carries the conversation, and the browser would send it off on its own.
 */
function MarkdownImage({ src, alt }: { src?: string; alt?: string }) {
  const host = externalHost(src);
  const [show, setShow] = useState(!host);
  if (show && src) return <img src={src} alt={alt ?? ""} referrerPolicy="no-referrer" className="my-2 max-h-96 max-w-full rounded-lg" />;
  return (
    <button
      type="button"
      onClick={() => setShow(true)}
      className="my-1 inline-flex max-w-full items-center gap-2 rounded-lg border border-dashed border-zinc-300 px-3 py-2 text-left text-sm text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800/60"
      title={src}
    >
      <ImageIcon size={16} className="shrink-0" />
      <span className="min-w-0 truncate">
        {alt ? `${alt} · ` : ""}Image from <span className="font-medium">{host}</span>
      </span>
      <span className="shrink-0 text-indigo-600 dark:text-indigo-400">Load</span>
    </button>
  );
}

function Markdown({ text, streaming, codeAction, codeView }: { text: string; streaming?: boolean; codeAction?: CodeAction; codeView?: CodeView }) {
  return (
    <div className="prose-chat">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: false }]]}
        rehypePlugins={[rehypeKatex]}
        components={{
          pre: ({ children }) => <>{children}</>,
          code: ({ className, children }) => {
            const match = /language-([\w+#-]+)/.exec(className ?? "");
            const code = String(children ?? "");
            const block = match || code.includes("\n");
            if (!block) return <code className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-[0.9em] dark:bg-zinc-800">{children}</code>;
            return <CodeBlock lang={match?.[1] ?? ""} code={code.replace(/\n$/, "")} streaming={streaming} action={codeAction} view={codeView} />;
          },
          img: ({ src, alt }) => <MarkdownImage src={typeof src === "string" ? src : undefined} alt={alt} />,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noreferrer noopener" className="text-indigo-600 underline underline-offset-2 dark:text-indigo-400">
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="my-3 overflow-x-auto">
              <table>{children}</table>
            </div>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

export default memo(Markdown);
